import { useCallback } from 'react';
import { rsvpService } from '../../../lib/supabase';
import { createLogger } from '../../../lib/logger';
import { RSVPResponseWithTable } from '../types';

const log = createLogger('rsvp');

interface UseBatchActionsParams {
  userId: string | undefined;
  rsvpResponses: RSVPResponseWithTable[];
  selectedItems: Set<string>;
  activeTab: string;
  onRefresh: () => Promise<void>;
  onDataChange?: () => void;
  clearSelection: () => void;
  setBatchDeleting: (v: boolean) => void;
  checkGuestSeatingStatus: (rsvpId: string) => { hasSeating: boolean; tableName?: string; tableId?: number };
  clearGuestFromSeating: (rsvpId: string) => Promise<void>;
}

export function useBatchActions({
  userId,
  rsvpResponses,
  selectedItems,
  activeTab,
  onRefresh,
  onDataChange,
  clearSelection,
  setBatchDeleting,
  checkGuestSeatingStatus,
  clearGuestFromSeating,
}: UseBatchActionsParams) {
  const handleBatchDelete = useCallback(async () => {
    if (selectedItems.size === 0 || !userId) return;

    const selectedResponses = rsvpResponses.filter(r => selectedItems.has(r.id));
    const withSeating = selectedResponses.filter(r => r.tableId).length;

    let confirmMessage = '';
    if (activeTab === 'messages') {
      confirmMessage = `確定要刪除選中的 ${selectedItems.size} 筆賓客記錄嗎？\n\n⚠️ 注意：這將完全刪除賓客的 RSVP 記錄，包括：\n• 賓客資訊和聯絡方式\n• 出席狀態和同行人數\n• 餐食需求和留言內容`;
      if (withSeating > 0) {
        confirmMessage += `\n• 座位安排（${withSeating} 位賓客）`;
      }
      confirmMessage += '\n\n此操作無法撤銷！';
    } else {
      confirmMessage = `確定要刪除選中的 ${selectedItems.size} 筆 RSVP 回覆嗎？此操作無法撤銷。`;
      if (withSeating > 0) {
        confirmMessage += `\n\n其中 ${withSeating} 位賓客已安排座位，刪除將同時清理座位安排。`;
      }
    }

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    try {
      setBatchDeleting(true);
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      for (const itemId of selectedItems) {
        try {
          const response = rsvpResponses.find(r => r.id === itemId);
          if (!response) continue;

          const seatingStatus = checkGuestSeatingStatus(itemId);

          if (seatingStatus.hasSeating) {
            await clearGuestFromSeating(itemId);
          }

          await rsvpService.deleteRSVP(itemId, userId);
          successCount++;

        } catch (error) {
          errorCount++;
          const response = rsvpResponses.find(r => r.id === itemId);
          const guestName = response?.guestName || '未知賓客';
          errors.push(`${guestName}: ${error instanceof Error ? error.message : '刪除失敗'}`);
        }
      }

      await onRefresh();
      onDataChange?.();
      clearSelection();

      let resultMessage = `批次刪除完成！\n\n成功刪除: ${successCount} 筆`;
      if (errorCount > 0) {
        resultMessage += `\n失敗: ${errorCount} 筆`;
        if (errors.length > 0) {
          resultMessage += `\n\n失敗詳情:\n${errors.slice(0, 5).join('\n')}`;
          if (errors.length > 5) {
            resultMessage += `\n... 及另外 ${errors.length - 5} 個錯誤`;
          }
        }
      }
      alert(resultMessage);

    } catch (error) {
      log.error('批次刪除錯誤:', error);
      alert(`批次刪除失敗：${error instanceof Error ? error.message : '請稍後再試'}`);
    } finally {
      setBatchDeleting(false);
    }
  }, [selectedItems, userId, rsvpResponses, activeTab, onRefresh, onDataChange, clearSelection, setBatchDeleting, checkGuestSeatingStatus, clearGuestFromSeating]);

  const handleBatchClearMessages = useCallback(async () => {
    if (selectedItems.size === 0 || !userId) return;

    const confirmMessage = `確定要清空選中的 ${selectedItems.size} 則留言嗎？\n\n此操作只會清空留言內容，不會刪除賓客的 RSVP 記錄和座位安排。`;

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    try {
      setBatchDeleting(true);
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      for (const itemId of selectedItems) {
        try {
          await rsvpService.clearRSVPMessage(itemId, userId);
          successCount++;
        } catch (error) {
          errorCount++;
          const response = rsvpResponses.find(r => r.id === itemId);
          const guestName = response?.guestName || '未知賓客';
          errors.push(`${guestName}: ${error instanceof Error ? error.message : '清空失敗'}`);
        }
      }

      await onRefresh();
      onDataChange?.();
      clearSelection();

      let resultMessage = `批次清空留言完成！\n\n成功清空: ${successCount} 則留言`;
      if (errorCount > 0) {
        resultMessage += `\n失敗: ${errorCount} 則`;
        if (errors.length > 0) {
          resultMessage += `\n\n失敗詳情:\n${errors.slice(0, 5).join('\n')}`;
          if (errors.length > 5) {
            resultMessage += `\n... 及另外 ${errors.length - 5} 個錯誤`;
          }
        }
      }
      alert(resultMessage);

    } catch (error) {
      log.error('批次清空留言錯誤:', error);
      alert(`批次清空留言失敗：${error instanceof Error ? error.message : '請稍後再試'}`);
    } finally {
      setBatchDeleting(false);
    }
  }, [selectedItems, userId, rsvpResponses, onRefresh, onDataChange, clearSelection, setBatchDeleting]);

  return {
    handleBatchDelete,
    handleBatchClearMessages,
  };
}
